import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import PublicNavbar from './common/PublicNavbar';
import PublicFooter from './common/PublicFooter';
import { siteConfigAPI } from '../services/api';

interface PageData {
  id: number;
  title: string;
  slug: string;
  content: string;
  meta_title: string;
  meta_description: string;
  updated_at: string;
}

const StaticPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [pageState, setPageState] = useState<{
    page: PageData | null;
    loading: boolean;
    notFound: boolean;
  }>({ page: null, loading: true, notFound: false });
  const { page, loading, notFound } = pageState;

  useEffect(() => {
    if (!slug) return;
    setPageState(prev => ({ ...prev, loading: true, notFound: false }));
    siteConfigAPI.getPage(slug)
      .then(res => {
        setPageState(prev => ({ ...prev, page: res.data, loading: false }));
        document.title = res.data.meta_title || res.data.title;
      })
      .catch(() => setPageState(prev => ({ ...prev, notFound: true, loading: false })));
  }, [slug]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicNavbar />

      {/* Hero header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
          paddingTop: '120px',
          paddingBottom: '48px',
          textAlign: 'center',
        }}
      >
        <Container>
          {loading ? (
            <Spinner animation="border" variant="light" />
          ) : notFound ? (
            <h1 style={{ color: 'white', fontWeight: 800, fontSize: '2rem' }}>
              Page Not Found
            </h1>
          ) : (
            <h1
              style={{
                color: 'white',
                fontWeight: 800,
                fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
                letterSpacing: '-0.5px',
                marginBottom: '8px',
              }}
            >
              {page?.title}
            </h1>
          )}
          {page && (
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', marginBottom: 0 }}>
              Last updated {new Date(page.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
        </Container>
      </div>

      {/* Content */}
      <div style={{ flex: 1, background: '#f8fafc', padding: '48px 0 80px' }}>
        <Container style={{ maxWidth: '800px' }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : notFound ? (
            <div className="text-center py-5">
              <p style={{ color: '#64748b', fontSize: '1.1rem' }}>
                The page you're looking for doesn't exist or has been unpublished.
              </p>
            </div>
          ) : (
            <div
              className="static-page-content"
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: 'clamp(24px, 5vw, 48px)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                lineHeight: 1.8,
                color: '#334155',
                fontSize: '1rem',
              }}
              dangerouslySetInnerHTML={{ __html: page?.content || '' }}
            />
          )}
        </Container>
      </div>

      <PublicFooter />

      <style>{`
        .static-page-content h1,
        .static-page-content h2,
        .static-page-content h3 {
          color: #0f172a;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          font-weight: 700;
        }
        .static-page-content h1 { font-size: 1.6rem; }
        .static-page-content h2 { font-size: 1.3rem; }
        .static-page-content h3 { font-size: 1.1rem; }
        .static-page-content p { margin-bottom: 1rem; }
        .static-page-content ul, .static-page-content ol {
          padding-left: 1.5rem;
          margin-bottom: 1rem;
        }
        .static-page-content li { margin-bottom: 0.4rem; }
        .static-page-content a {
          color: #2563eb;
          text-decoration: underline;
        }
        .static-page-content strong { color: #1e293b; }
        .static-page-content hr {
          border: none;
          border-top: 1px solid #e2e8f0;
          margin: 2rem 0;
        }
      `}</style>
    </div>
  );
};

export default StaticPage;
